import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '@app/+auth/shared/services/auth.service';
import { environment } from '@environments/environment';
import {Observable, throwError, Subject, BehaviorSubject} from 'rxjs';
import { Router } from '@angular/router';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { Path } from '@core/enums';
import { ToastrService } from 'ngx-toastr';
import * as moment from 'moment';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private isRefreshingToken = false;

  constructor(
    private authService: AuthService,
    private toasterService: ToastrService,
    private router: Router
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const tokenObj = this.getStoredToken();
    const isApiUrl = request.url.startsWith(environment.apiUrl);

    if (tokenObj && isApiUrl) {
      request = this.addAuthHeaders(request, tokenObj.accessToken, tokenObj.refreshToken);
    }

    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => this.handleAuthError(err, request, next))
    );
  }

  private handleAuthError(
    err: HttpErrorResponse,
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<any> {
    if (err.status === 401 || err.status === 403) {
      const tokenObj = this.getStoredToken();

      if (tokenObj == null) {
        return throwError(err);
      }

      if (this.isTokenExpired(tokenObj.expiresIn)) {
        if (!this.isRefreshingToken) {
          this.isRefreshingToken = true;
          this.refreshTokenSubject.next(null); // Reset the subject

          return this.refreshToken(tokenObj).pipe(
            switchMap((newTokenObj) => {
              this.isRefreshingToken = false;
              this.storeToken(newTokenObj.body.tokenObj);
              this.refreshTokenSubject.next(newTokenObj.body.tokenObj.accessToken);

              // Retry the original request with the new token
              request = this.addAuthHeaders(
                request,
                newTokenObj.body.tokenObj.accessToken,
                newTokenObj.body.tokenObj.refreshToken
              );
              return next.handle(request);
            }),
            catchError((refreshError) => {
              console.log('Catching the refreshError', refreshError);
              this.isRefreshingToken = false;
              this.handleLogout();
              return throwError(refreshError);
            })
          );
        } else {
          console.log('In else block');
          // Wait for the refresh to complete
          return this.refreshTokenSubject.pipe(
            filter((token) => !!token), // Wait for a non-null token
            take(1), // Take the first emitted token
            switchMap((newAccessToken) => {
              request = this.addAuthHeaders(request, newAccessToken, tokenObj.refreshToken);
              return next.handle(request);
            })
          );
        }
      } else {
        return throwError(err); // Token is valid but still failed
      }
    }

    return throwError(err); // For non-auth-related errors
  }

  private refreshToken(tokenObj: any): Observable<any> {
    const userInformation = JSON.parse(localStorage.getItem('_user'));
    return this.authService.getRefreshToken({
      email: userInformation.email,
      userId: userInformation._id,
      refreshToken: tokenObj.refreshToken,
    });
  }

  private addAuthHeaders(request: HttpRequest<any>, accessToken: string, refreshToken: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
        refreshtoken: refreshToken,
      },
    });
  }

  private getStoredToken(): any {
    return JSON.parse(localStorage.getItem('_token'));
  }

  private storeToken(tokenObj: any): void {
    localStorage.setItem('_token', JSON.stringify(tokenObj));
  }

  private handleLogout(): Observable<never> {
    this.toasterService.info('Please log in again.', 'Session Expired');
    this.authService.logout().subscribe((response) => {
    }, (error) => {
      console.log('Ignore the error from logout');
    });
    this.authService.logoutLocal();
    this.router.navigate([`/${Path.SignIn}`]);
    return throwError('Session expired, please log in again.');
  }

  private isTokenExpired(expiresIn: number): boolean {
    const expirationTime = moment(expiresIn);
    return moment().isAfter(expirationTime.subtract(1, 'minutes'));
  }
}
